/**
 * files.api.ts
 *
 * React Query hooks for the files feature:
 *   - useEntityFiles   — infinite-scroll list query (manages pagination internally)
 *   - useFileUpload    — upload mutation with optimistic placeholder tiles
 *   - useFileDelete    — delete mutation with optimistic removal from cache
 *   - useFileUpdate    — partial update mutation
 *   - useFileCreate    — create a file record linked to an entity
 *   - useFileUrl       — resolve a file's display URL via presigned-URL API
 *
 * Pagination contract
 * -------------------
 * useEntityFiles drives all offset state internally via useInfiniteQuery. The
 * calling component never manages offsets directly — it calls fetchNextPage()
 * when the user scrolls to the bottom and reads hasNextPage to decide whether
 * to show a "load more" trigger.
 *
 * onSuccess / onError / onSettled contract
 * ----------------------------------------
 * mutationFn   — async, use await for every operation whose result the next
 *                step depends on (uploads, API calls).
 * onMutate     — async, awaited by React Query before calling mutationFn.
 *                Used to cancel in-flight queries and seed optimistic state.
 * onSuccess    — side-effects after a confirmed success (toast).
 * onError      — side-effects + rollback after a confirmed failure (toast, cache restore).
 * onSettled    — always runs; used for cleanup and cache invalidation.
 *                queryClient.invalidateQueries() returns a Promise but we do
 *                not need to block on it, so it is called without await.
 */

import { useState, useMemo } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  InfiniteData,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { uploadFile, getPresignedImageUrlForFile } from "@/network/network";
import type { SwivuFile, SwivuFileListPage } from "@/react/components/entityViews/files/types/swivuFile";
import { queryClient } from "@/lib/queryClient";
import { fileKeys, FileListFilters } from "./files.query-keys";
import {
  getFileList,
  getFileById,
  deleteFileById,
  updateFileById,
  createFile,
  SwivuFileUpdatePayload,
} from "./files.service";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** Entity types that can own files */
export type FileEntityType = "task" | "resource" | "client" | "location";

interface FileEntityParams {
  boardId: number;
  entityType: FileEntityType;
  entityId: number;
}

/**
 * Resolve a single file for an entity. Prefers React Query list cache, then
 * pages GET /file/list (there is no GET /file/{id}).
 */
export async function resolveEntityFile({
  boardId,
  entityType,
  entityId,
  fileId,
}: FileEntityParams & { fileId: number }): Promise<SwivuFile> {
  const cachedQueries = queryClient.getQueriesData<InfiniteData<SwivuFileListPage>>({
    queryKey: fileKeys.entity(boardId, entityType, entityId),
  });

  for (const [, data] of cachedQueries) {
    const found = data?.pages.flatMap((page) => page.results).find((file) => file.id === fileId);
    if (found) return found;
  }

  return getFileById({ fileId, boardId, entityType, entityId });
}

/** Returns `"1 file"` or `"N files"` */
function pluralizeFile(n: number): string {
  return `${n} ${n === 1 ? "file" : "files"}`;
}

// ---------------------------------------------------------------------------
// useFileUrl — resolve a file's URL via presigned-URL API
// ---------------------------------------------------------------------------

/**
 * The resolved URL result for a single file.
 */
export interface FileUrlResult {
  url: string | null;
  thumbnailUrl: string | null;
  /** Resolved display URL: thumbnailUrl if available, falling back to url. */
  displayUrl: string | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Resolves a file's display URL via GET /file/{id}/presigned-url.
 *
 * Safe to call with `null` / `undefined` — the query is disabled and
 * `isLoading` stays `false`.
 *
 * Cache timings: staleTime 5 min / gcTime 6 min (presigned URLs expire).
 */
export function useFileUrl(fileId: number | null | undefined): FileUrlResult {
  const { data, isLoading, isError } = useQuery({
    queryKey: fileKeys.fileUrl(fileId!),
    queryFn: async () => {
      const response = await getPresignedImageUrlForFile(fileId!);
      return response.data as { url: string; thumbnail_url: string };
    },
    enabled: fileId != null,
    staleTime: 5 * 60_000,
    gcTime: 6 * 60_000,
    retry: 1,
  });

  return {
    url: data?.url ?? null,
    thumbnailUrl: data?.thumbnail_url ?? data?.url ?? null,
    displayUrl: data?.thumbnail_url ?? data?.url ?? null,
    isLoading: fileId != null ? isLoading : false,
    isError,
  };
}

/**
 * Builds a stable negative ID for an optimistic placeholder so it never
 * clashes with a real server ID (positive integer).
 */
function optimisticId(seed: number, index: number): number {
  return -(seed + index);
}

function createOptimisticFile(
  file: File,
  params: FileEntityParams,
  id: number
): SwivuFile {
  return {
    id,
    document: null,
    url: URL.createObjectURL(file),
    thumbnailUrl: null,
    name: file.name,
    task: params.entityType === "task" ? params.entityId : null,
    resource: params.entityType === "resource" ? params.entityId : null,
    client: params.entityType === "client" ? params.entityId : null,
    location: params.entityType === "location" ? params.entityId : null,
    order: 0,
    description: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    creator: null,
    updater: null,
    sizeBytes: file.size,
    board: params.boardId,
    fileType: file.type.startsWith("image/") ? "image" : "document",
    source: "swivu",
    creatorName: null,
    updaterName: null,
    hasPermission: null,
    canAnalyze: null,
    externalId: null,
    canViewGrant: 5,
    canEditGrant: 5,
    isAnnotated: false,
    isOptimistic: true,
  };
}

// ---------------------------------------------------------------------------
// useEntityFiles
// ---------------------------------------------------------------------------

export interface UseEntityFilesParams extends FileEntityParams {
  filters?: FileListFilters;
  /** Pass false to disable the query (e.g. when the panel is closed) */
  enabled?: boolean;
  /** Override default 30s staleTime (e.g. faster while awaiting CompanyCam import). */
  staleTime?: number;
  /** Override default 60s poll; pass false to disable polling. */
  refetchInterval?: number | false;
}

export interface UseEntityFilesResult {
  /** Flat list of all loaded files across all pages */
  files: SwivuFile[];
  /** Total count reported by the server (first page) */
  totalCount: number;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Infinite-scroll query for entity files.
 *
 * Pagination is fully internal: React Query tracks the current offset via
 * pageParams (initialPageParam = 0). getNextPageParam derives the next offset
 * from the cumulative length of all loaded results vs. the total count.
 *
 * Default staleTime: 30 s — avoids hammering the API on rapid re-renders.
 * Default refetchInterval: 60 s — background poll (no WS push). Callers can
 * tighten both briefly after a CompanyCam link so imports show up sooner.
 */
export function useEntityFiles({
  boardId,
  entityType,
  entityId,
  filters = {},
  enabled = true,
  staleTime = 30_000,
  refetchInterval = 60_000,
}: UseEntityFilesParams): UseEntityFilesResult {
  const queryKey = fileKeys.entityFiltered(boardId, entityType, entityId, filters);

  const result = useInfiniteQuery<
    SwivuFileListPage,
    Error,
    InfiniteData<SwivuFileListPage>,
    ReturnType<typeof fileKeys.entityFiltered>,
    number
  >({
    queryKey,
    queryFn: ({ pageParam }) =>
      getFileList({
        board_id: boardId,
        [entityType]: entityId,
        ...filters,
        limit: 50,
        offset: pageParam,
      }),
    initialPageParam: 0,
    // Use lastPageParam (the offset that produced lastPage) so that even when
    // maxPages trims older pages from the cache, the next offset always advances
    // by one page rather than re-fetching the last-visible offset indefinitely.
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const nextOffset = lastPageParam + 50;
      return nextOffset < lastPage.count ? nextOffset : undefined;
    },
    staleTime,
    refetchInterval,
    enabled,
  });

  const files = useMemo(
    () => result.data?.pages.flatMap((p) => p.results) ?? [],
    [result.data]
  );

  const totalCount = result.data?.pages[0]?.count ?? 0;

  return {
    files,
    totalCount,
    fetchNextPage: result.fetchNextPage,
    hasNextPage: result.hasNextPage ?? false,
    isFetchingNextPage: result.isFetchingNextPage,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isError: result.isError,
    error: result.error,
    refetch: result.refetch,
  };
}

// ---------------------------------------------------------------------------
// useFileUpload
// ---------------------------------------------------------------------------

export interface UseFileUploadParams extends FileEntityParams {
  /**
   * Called when all files in a batch have been committed to the server.
   * Useful for post-upload side-effects (e.g. re-opening the file panel).
   */
  onUploaded?: () => void;
}

export interface UseFileUploadResult {
  /** Upload one or more files. Returns after all REST calls complete. */
  uploadFiles: (files: FileList | File[]) => Promise<void>;
  /** Placeholder files for in-progress uploads (one entry per file) */
  optimisticFiles: SwivuFile[];
  isUploading: boolean;
}

/**
 * File upload mutation with optimistic UI.
 *
 * Flow:
 *  1. onMutate   — cancel queries; add placeholder tiles to local state.
 *  2. mutationFn — upload each file in parallel (await Promise.all);
 *                  call createFile for each document to link to the entity.
 *  3. onError    — remove placeholders; show error toast.
 *  4. onSuccess  — show success toast; call optional onUploaded callback.
 *  5. onSettled  — always: remove placeholders; revoke blob URLs; invalidate entity queries.
 */
export function useFileUpload({
  boardId,
  entityType,
  entityId,
  onUploaded,
}: UseFileUploadParams): UseFileUploadResult {
  /**
   * Map<batchId, SwivuFile[]> — tracks pending upload batches so concurrent
   * uploads don't clobber each other's placeholders.
   */
  const [pendingBatches, setPendingBatches] = useState<Map<string, SwivuFile[]>>(
    new Map()
  );

  const entityQueryKey = fileKeys.entity(boardId, entityType, entityId);

  const mutation = useMutation<void, Error, FileList | File[], { batchId: string }>({
    mutationFn: async (rawFiles) => {
      const files = Array.from(rawFiles);
      toast.loading(`Uploading ${pluralizeFile(files.length)}…`, { id: "file-upload" });

      // Upload all files in parallel; each upload returns the new document id
      const documentIds = await Promise.all(
        files.map(async (file) => {
          const { data } = await uploadFile(file, file.name);
          return { documentId: data.id as number, name: file.name };
        })
      );

      // Link each uploaded document to the entity via POST /file/create
      await Promise.all(
        documentIds.map(({ documentId, name }) =>
          createFile({
            document: documentId,
            board_id: boardId,
            [entityType]: entityId,
            name,
          })
        )
      );
    },

    onMutate: async (rawFiles) => {
      // Cancel any in-flight refetches so they don't overwrite our UI state
      await queryClient.cancelQueries({ queryKey: entityQueryKey });

      const batchId = crypto.randomUUID();
      const seed = Date.now();
      const placeholders = Array.from(rawFiles).map((file, i) =>
        createOptimisticFile(file, { boardId, entityType, entityId }, optimisticId(seed, i))
      );

      setPendingBatches((prev) => new Map(prev).set(batchId, placeholders));

      return { batchId };
    },

    onSuccess: (_data, rawFiles) => {
      toast.success(`${pluralizeFile(rawFiles.length)} uploaded`, { id: "file-upload" });
      onUploaded?.();
    },

    onError: (_error, rawFiles) => {
      toast.error(`Failed to upload ${pluralizeFile(rawFiles.length)}. Please try again.`, {
        id: "file-upload",
      });
    },

    onSettled: async (_data, _error, _variables, context) => {
      // Wait for refetch so placeholders don't vanish before real tiles appear.
      await queryClient.invalidateQueries({ queryKey: entityQueryKey });
      if (context?.batchId) {
        setPendingBatches((prev) => {
          const next = new Map(prev);
          // Revoke blob URLs created for optimistic placeholders
          next.get(context.batchId)?.forEach((f) => {
            if (f.url.startsWith("blob:")) URL.revokeObjectURL(f.url);
          });
          next.delete(context.batchId);
          return next;
        });
      }
    },
  });

  const optimisticFiles = useMemo(
    () => [...pendingBatches.values()].flat(),
    [pendingBatches]
  );

  return {
    uploadFiles: (files) => mutation.mutateAsync(files),
    optimisticFiles,
    isUploading: mutation.isPending,
  };
}

// ---------------------------------------------------------------------------
// useFileDelete
// ---------------------------------------------------------------------------

export type UseFileDeleteParams = FileEntityParams;

export interface UseFileDeleteResult {
  deleteFile: (fileId: number) => Promise<void>;
  isDeleting: boolean;
}

/**
 * File delete mutation with optimistic cache removal.
 *
 * Flow:
 *  1. onMutate   — cancel queries; snapshot all matching queries; remove file
 *                  from every matching infinite query page optimistically.
 *  2. mutationFn — call DELETE /file/{id}/delete.
 *  3. onError    — restore all snapshots; show error toast.
 *  4. onSuccess  — show success toast.
 *  5. onSettled  — invalidate entity queries (refetch to confirm true state).
 */
export function useFileDelete({
  boardId,
  entityType,
  entityId,
}: UseFileDeleteParams): UseFileDeleteResult {
  const entityQueryKey = fileKeys.entity(boardId, entityType, entityId);

  const mutation = useMutation<
    void,
    Error,
    number,
    { snapshots: [ReturnType<typeof fileKeys.entityFiltered>, InfiniteData<SwivuFileListPage> | undefined][] }
  >({
    mutationFn: (fileId) => deleteFileById(fileId),

    onMutate: async (fileId) => {
      await queryClient.cancelQueries({ queryKey: entityQueryKey });

      // Snapshot ALL matching filter variants for complete rollback on error
      const snapshots = queryClient.getQueriesData<InfiniteData<SwivuFileListPage>>(
        { queryKey: entityQueryKey }
      ) as [ReturnType<typeof fileKeys.entityFiltered>, InfiniteData<SwivuFileListPage> | undefined][];

      // Optimistically remove the file only from caches that contain it.
      // Sibling filter variants (e.g. image vs document) must not get a
      // spurious count-- that can hide Load More until invalidate finishes.
      queryClient.setQueriesData<InfiniteData<SwivuFileListPage>>(
        { queryKey: entityQueryKey },
        (old) => {
          if (!old) return old;
          const existed = old.pages.some((page) => page.results.some((f) => f.id === fileId));
          if (!existed) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              count: Math.max(0, page.count - 1),
              results: page.results.filter((f) => f.id !== fileId),
            })),
          };
        }
      );

      return { snapshots };
    },

    onError: (_error, _fileId, context) => {
      // Restore all filter variants from snapshots
      context?.snapshots.forEach(([key, data]) => {
        if (data !== undefined) queryClient.setQueryData(key, data);
      });
      toast.error("Failed to delete file. Please try again.");
    },

    onSuccess: () => {
      toast.success("File deleted.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: entityQueryKey });
    },
  });

  return {
    deleteFile: (fileId) => mutation.mutateAsync(fileId),
    isDeleting: mutation.isPending,
  };
}

// ---------------------------------------------------------------------------
// useFileUpdate — partial update via PATCH /file/{id}/update
// ---------------------------------------------------------------------------

/**
 * Mutation hook for partially updating a Swivu file record.
 *
 * On success the updated file is patched directly into every matching
 * infinite-query page in the cache so no full re-fetch is required.
 */
export function useFileUpdate() {
  return useMutation({
    mutationFn: ({ fileId, payload }: { fileId: number; payload: SwivuFileUpdatePayload }) =>
      updateFileById(fileId, payload),

    onSuccess: (updatedFile) => {
      queryClient.setQueriesData<InfiniteData<SwivuFileListPage>>(
        { queryKey: fileKeys.all, exact: false },
        (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              results: page.results.map((f) =>
                f.id === updatedFile.id ? updatedFile : f,
              ),
            })),
          };
        },
      );
    },

    onError: () => {
      toast.error("Failed to update file. Please try again.");
    },
  });
}

// ---------------------------------------------------------------------------
// useFileCreate — create a file record linked to an entity
// ---------------------------------------------------------------------------

/**
 * Standalone mutation hook for creating a file record outside the entity
 * upload flow (e.g. ImageAnnotationEditor, SpotIncentive, MyMedia assignment).
 */
export function useFileCreate() {
  return useMutation({
    mutationFn: createFile,

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fileKeys.all });
    },

    onError: () => {
      toast.error("Failed to create file. Please try again.");
    },
  });
}

// ---------------------------------------------------------------------------
// useUserFiles — fetch all files created by a specific board user
// ---------------------------------------------------------------------------

export interface UseUserFilesParams {
  boardId: number;
  creatorId: number;
  enabled?: boolean;
  staleTime?: number;
}

export interface UseUserFilesResult {
  files: SwivuFile[];
  totalCount: number;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
}

const USER_FILES_PAGE_SIZE = 50;

/**
 * Paginated infinite-scroll query for all files on a board created by a
 * specific user, using the `creator_id` filter on GET /file/list.
 *
 * Mirrors the useEntityFiles pattern. Callers receive a flat `files` array
 * plus the standard pagination handles (`fetchNextPage`, `hasNextPage`,
 * `isFetchingNextPage`) to wire up an InfiniteScroll sentinel.
 */
export function useUserFiles({
  boardId,
  creatorId,
  enabled = true,
  staleTime = 30_000,
}: UseUserFilesParams): UseUserFilesResult {
  const result = useInfiniteQuery<
    SwivuFileListPage,
    Error,
    InfiniteData<SwivuFileListPage>,
    readonly unknown[],
    number
  >({
    queryKey: ["userFiles", boardId, creatorId],
    queryFn: ({ pageParam }) =>
      getFileList({
        board_id: boardId,
        creator_id: creatorId,
        limit: USER_FILES_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const nextOffset = lastPageParam + USER_FILES_PAGE_SIZE;
      return nextOffset < lastPage.count ? nextOffset : undefined;
    },
    enabled: enabled && !!boardId && !!creatorId,
    staleTime,
  });

  const files = useMemo(
    () => result.data?.pages.flatMap((p) => p.results) ?? [],
    [result.data],
  );

  return {
    files,
    totalCount: result.data?.pages[0]?.count ?? 0,
    fetchNextPage: result.fetchNextPage,
    hasNextPage: result.hasNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    isLoading: result.isLoading,
    isError: result.isError,
  };
}
